import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Button, Title, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SettingsService, { type SupportContact } from '../../services/settings/settingsService.ts';
import { isAdmin } from '../../core/roles.ts';
import CreditSettingsCard from './CreditSettingsCard.tsx';
import * as Sentry from '@sentry/react';

const settingsService = new SettingsService();

/**
 * The support number customers reach from the WhatsApp button in the app.
 *
 * It is a settings page rather than a react-admin Resource because there is one
 * row, not a list: a Resource would render an index and a create button for a
 * thing that can only ever be edited.
 */
const SettingsPage = () => {
    const { t } = useTranslation();
    const notify = useNotify();

    // The route and the menu entry are already gated, but typing /settings into
    // the address bar bypasses both. Checked here too so a vendor is never shown
    // a form they cannot submit - the backend would reject the PATCH, and an
    // error after pressing Save is a worse answer than not offering the field.
    const permitted = isAdmin();

    const [number, setNumber] = useState('');
    const [saved, setSaved] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!permitted) { setLoading(false); return; }

        let cancelled = false;

        settingsService.getSupportContact()
            .then((contact: SupportContact) => {
                if (cancelled) return;
                setNumber(contact.whatsappNumber ?? '');
                setSaved(contact.whatsappNumber ?? null);
            })
            .catch((e) => {
                Sentry.captureException(e);
                if (!cancelled) {
                    notify(t('settings.support.loadFailed', 'Could not load the current number'), { type: 'error' });
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [permitted, notify, t]);

    // Checked here as well as on the server so a typo is caught before it is
    // saved, not after customers have been sent somewhere that does not answer.
    const looksValid = (value: string) => /^\+[0-9][0-9\s\-()]{6,}$/.test(value.trim());

    const onSave = useCallback(async () => {
        const value = number.trim();

        if (!looksValid(value)) {
            setError(t('settings.support.invalid', 'Include the country code, for example +357 99 123 456'));
            return;
        }

        setError(null);
        setSaving(true);

        try {
            const contact = await settingsService.updateSupportContact(value);
            // Render what the server stored, not what was typed: it normalises
            // the number, so these can legitimately differ.
            setNumber(contact.whatsappNumber ?? '');
            setSaved(contact.whatsappNumber ?? null);
            notify(t('settings.support.saved', 'Saved. Customers will see the new number from their next app launch.'), { type: 'success' });
        } catch (e: any) {
            const message = e?.response?.status === 400
                ? t('settings.support.invalid', 'Include the country code, for example +357 99 123 456')
                : t('common.saveFailed', 'Save failed');
            setError(message);
            notify(message, { type: 'error' });
        } finally {
            setSaving(false);
        }
    }, [number, notify, t]);

    const dirty = number.trim() !== (saved ?? '');

    if (!permitted) {
        return (
            <>
                <Title title={t('resources.settings', 'Settings')} />
                <Card sx={{ mt: 2, maxWidth: 640 }}>
                    <CardContent>
                        <Typography variant="body1">
                            {t('settings.notPermitted', 'You do not have permission to view this page.')}
                        </Typography>
                    </CardContent>
                </Card>
            </>
        );
    }

    return (
        <>
            <Title title={t('resources.settings', 'Settings')} />
            <Card sx={{ mt: 2, maxWidth: 640 }}>
                <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <WhatsAppIcon sx={{ color: '#25D366' }} />
                        <Typography variant="h6">
                            {t('settings.support.title', 'Support WhatsApp number')}
                        </Typography>
                    </Stack>

                    {loading ? (
                        <Box sx={{ py: 3 }}><CircularProgress size={24} /></Box>
                    ) : (
                        <>
                            <TextField
                                fullWidth
                                value={number}
                                onChange={(e) => { setNumber(e.target.value); setError(null); }}
                                placeholder="+357 99 123 456"
                                error={!!error}
                                helperText={error ?? t(
                                    'settings.support.help',
                                    'Customers who tap the WhatsApp button in the app are sent to this number. It takes effect on their next app launch. The country code is required.'
                                )}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                                            </InputAdornment>
                                        )
                                    }
                                }}
                                sx={{ maxWidth: 420 }}
                            />

                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    disabled={saving || !dirty}
                                    onClick={onSave}
                                    label={saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                                />
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>

            <CreditSettingsCard />
        </>
    );
};

export default SettingsPage;
